import { Loader2Icon } from 'lucide-react';
import { useEffect } from 'react';

import { ActiveProgress } from '@/api/types/telemetry';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface InlineProgressProps {
  className?: string;
  // Codebase id — keys the per-codebase high-water mark. When omitted the
  // floor clamp is a no-op; production library-row usage always supplies it.
  codebaseId?: string;
  // Fallback label when there's no percentage yet (telemetry loading OR queued).
  fallbackVerbText: string;
  // null = queued (telemetry loaded, no flow active); undefined = telemetry
  // not loaded yet for this codebase.
  progress: ActiveProgress | null | undefined;
}

// Fixed width so the column doesn't shift as text length / percentage
// changes (e.g. "5%" vs "100%", "Connecting…" vs "Generating docs…").
const CONTAINER_WIDTH = 'w-[180px]';

// Module-level high-water mark per codebase. The backend can change
// `progress.flow` mid-generation (e.g. inspector → documenter); the
// composite percent occasionally drops at boundaries due to refetch races.
// We hold the highest value we've shown so the bar never goes backwards.
//
// Lifting the map outside the component lets the floor survive transient
// unmounts (view-mode toggle in SourcesList, brief settle-status flips,
// virtualization re-mounts) — the previous per-instance ref reset to 0 in
// those cases and the user saw the bar drop.
//
// `clearProgressHighWaterMark(codebaseId)` is exported so callers can reset
// the floor when a fresh generation cycle begins for a codebase that
// previously completed (e.g. user re-runs Generate Docs).
// `clearAllProgressHighWaterMarks` is for setupTests' afterEach so the Map
// doesn't leak across test files.
const progressHighWaterMarks = new Map<string, number>();

export function clearProgressHighWaterMark(codebaseId: string): void {
  progressHighWaterMarks.delete(codebaseId);
}

export function clearAllProgressHighWaterMarks(): void {
  progressHighWaterMarks.clear();
}

function readHighWaterFloor(codebaseId: string | undefined): number {
  if (codebaseId === undefined) return 0;
  return progressHighWaterMarks.get(codebaseId) ?? 0;
}

export function InlineProgress({ className, codebaseId, fallbackVerbText, progress }: InlineProgressProps) {
  // `progress.percent_complete` is a weighted aggregate computed
  // server-side. VersionProgress.percent_complete weights each phase by
  // PHASE_WEIGHTS[flow_type], and for versioned flows the API composes
  // inspector + deep_context with INSPECTOR_COMPOSITE_WEIGHT (0.70) +
  // DEEP_CONTEXT_COMPOSITE_WEIGHT (0.30). The value is already a clean
  // 0-100 overall aggregate — we just clamp + round it.
  const computedRaw = progress == null ? null : Math.max(0, Math.min(100, Math.round(progress.percent_complete)));

  // Write the high-water mark only after commit so a discarded render
  // (Suspense fallback, abandoned transition) can't bump the module-level
  // floor for a value the user never saw.
  useEffect(() => {
    if (codebaseId === undefined || computedRaw === null) return;
    const prior = progressHighWaterMarks.get(codebaseId) ?? 0;
    if (computedRaw > prior) progressHighWaterMarks.set(codebaseId, computedRaw);
  }, [codebaseId, computedRaw]);

  // Resolve a label + percent for each state. The displayed pct reads the
  // floor at render time, so even before the write effect runs the bar
  // shows the highest committed value for this codebase.
  let label: string;
  let pct: number | null;
  if (progress === undefined) {
    // Telemetry not yet loaded — show the verb fallback with a spinner.
    // Don't claim "Queued" prematurely.
    label = fallbackVerbText;
    pct = null;
  } else if (progress === null) {
    // Telemetry loaded, no active flow — codebase is genuinely queued.
    label = 'Queued';
    pct = null;
  } else {
    label = fallbackVerbText;
    pct = Math.max(readHighWaterFloor(codebaseId), computedRaw ?? 0);
  }

  // Spinner is always present while this component is mounted. The parent
  // (SourcesStatus) only renders InlineProgress for CONNECTING / GENERATING
  // statuses, so the row is always in some "we're working on this" state —
  // including Queued (the codebase is in line, even if no per-row flow is
  // active yet). A consistent spinner across all three internal sub-states
  // (telemetry-loading / queued / actively-progressing) reads as "wait" to
  // the user and avoids the prior subtle inconsistency where "Connecting…"
  // had a spinner but "Queued" didn't.
  return (
    <div className={cn(CONTAINER_WIDTH, 'relative', className)}>
      <div className="flex items-center gap-2 text-sm">
        <Loader2Icon className="text-driver-brand-blue h-4 w-4 shrink-0 animate-spin" />
        <span className={cn('truncate', pct === null && progress === null && 'text-muted-foreground')}>{label}</span>
        <span className="text-muted-foreground ml-auto shrink-0 tabular-nums">{pct === null ? '' : `${pct}%`}</span>
      </div>
      {pct !== null && (
        // Position the bar absolutely so its appearance/disappearance
        // doesn't change the container's height — the text row above
        // stays put. `inset-x-0` spans the full content-box of the
        // relative container without coupling to a specific width value.
        <Progress
          className="absolute inset-x-0 top-full mt-1 h-1"
          indicatorClassName="bg-driver-brand-blue"
          value={pct}
        />
      )}
    </div>
  );
}
